import { upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AnyObjectSchema } from 'yup';

import { useAuthUser } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { Steps } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useCliJobHandler } from 'core/hooks';
import { StepsProvider, useNotificationContext, useStepsContext } from 'core/states';
import { ExternalPartStatusType } from 'parts/enums';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { IBaseQuote } from 'quotes/types';
import { TAB_FULL_WIDTH } from 'theme/constants';
import { Color, Size } from 'theme/enums';

import { AddBomPartsToQuoteStep } from './enums';
import { SeeQuoteButton } from './SeeQuoteButton';
import { useAddBomPartsToQuoteContext } from './state';
import { SelectContact, SelectCustomerQuote, SelectItemsToCopy, SelectVendorQuote } from './Steps';
import { IAddBomPartsToQuoteProps } from './types';

interface IProps<TFormData>
  extends Omit<IAddBomPartsToQuoteProps<TFormData>, 'onClose' | 'onSubmitSuccess'> {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  itemsToCopyProps: {
    getSchema: (selectedQuote?: IBaseQuote) => AnyObjectSchema;
    fields: JSX.Element;
  };
}

export const AddBomPartsToQuoteSteps = <TFormData,>({
  isOpen,
  addToQuoteType,
  onClose,
  onSubmitSuccess,
  selectedItems,
  sourceQuote,
  entityType = QuoteEntityType.QUOTE,
  mapperFunction,
  addToCustomerQuoteAction,
  addToVendorQuoteAction,
  cliJobInProgressMessageKey,
  cliJobSuccessMessageKey,
  itemsToCopyProps,
}: IProps<TFormData>) => {
  const { t } = useTranslation();

  const itemsToCopyFormRef = useRef(null);

  const { showSuccessNotification } = useNotificationContext();
  const { selectedContact, setSelectedContact, selectedQuote, setSelectedQuote } =
    useAddBomPartsToQuoteContext();

  const { user } = useAuthUser();
  const { handleCliJob } = useCliJobHandler();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({
    user,
  });

  const handleOnClose = useCallback(() => {
    setSelectedContact(null);
    setSelectedQuote(null);
    onClose();
  }, [onClose, setSelectedContact, setSelectedQuote]);

  const onSubmitAddToQuote = useCallback(
    async (handleOnClose: () => void) => {
      const formData = await itemsToCopyFormRef.current.triggerFormSubmit();

      const action =
        QuoteType.VENDOR === addToQuoteType ? addToVendorQuoteAction : addToCustomerQuoteAction;

      const selectedItemsToCopy = !isAlternateVersionEnabled
        ? selectedItems
        : selectedItems.filter(
            (item) => item.externalPartStatus !== ExternalPartStatusType.ABANDONED
          );

      handleCliJob(
        action,
        mapperFunction({
          selectedItems: selectedItemsToCopy,
          targetQuote: selectedQuote,
          toQuoteType: addToQuoteType,
          data: formData,
          sourceQuote,
          entityType,
          isAlternateVersionEnabled,
        }),
        {
          cliJobInProgressMessage: t(cliJobInProgressMessageKey, {
            count: selectedItems.length,
          }),
          onCliJobFinished: (cliJob: ICliJob) => {
            let quoteId = selectedQuote?.id;

            if (!quoteId) {
              const cliResult = cliJob?.result as { quote: IBaseQuote } | { id: number }[];

              quoteId =
                (cliResult as { quote: IBaseQuote })?.quote?.id ||
                (cliResult as { id: number }[])?.[0]?.id;
            }

            showSuccessNotification(
              {
                message: t(cliJobSuccessMessageKey, { count: selectedItems.length }),
                buttons: [
                  <SeeQuoteButton key="seeQuoteButton" entityType={entityType} quoteId={quoteId} />,
                ],
                disableAutoClose: true,
              },
              NotificationPosition.TOP
            );

            onSubmitSuccess && onSubmitSuccess();
            handleOnClose();
          },
          notificationPosition: NotificationPosition.DRAWER,
        }
      );

      return false;
    },
    [
      addToCustomerQuoteAction,
      addToVendorQuoteAction,
      addToQuoteType,
      entityType,
      handleCliJob,
      isAlternateVersionEnabled,
      onSubmitSuccess,
      selectedItems,
      selectedQuote,
      showSuccessNotification,
      t,
      mapperFunction,
      sourceQuote,
      cliJobInProgressMessageKey,
      cliJobSuccessMessageKey,
    ]
  );

  const selectExistingQuoteStep = useMemo(() => {
    if (QuoteType.VENDOR === addToQuoteType) {
      return <SelectVendorQuote sourceQuote={sourceQuote} entityType={entityType} />;
    }

    if (QuoteType.CUSTOMER === addToQuoteType) {
      return <SelectCustomerQuote sourceQuote={sourceQuote} entityType={entityType} />;
    }

    return <></>;
  }, [addToQuoteType, entityType, sourceQuote]);

  const selectItemsToCopyTitle = useMemo(() => {
    if (selectedQuote?.id) {
      return t('titleSelectItemsToCopy');
    }

    return t('titleCreateNewQuoteAndSelectItemsToCopy', {
      entityType,
    });
  }, [selectedQuote?.id, t, entityType]);

  const steps = useMemo(
    () => [
      {
        type: AddBomPartsToQuoteStep.SELECT_CONTACT,
        title: t('titleSelectContact', { contactType: upperFirst(addToQuoteType) }),
        content: <SelectContact addToQuoteType={addToQuoteType} entityType={entityType} />,
        nextButton: {
          options: { disabled: !selectedContact },
        },
        sx: { width: '400px' },
      },
      {
        type: AddBomPartsToQuoteStep.SELECT_QUOTE,
        title: t('titleSelectQuote', { quoteType: upperFirst(addToQuoteType), entityType }),
        content: selectExistingQuoteStep,
        nextButton: {
          options: { disabled: !selectedQuote?.id },
        },
        sx: {
          ['@media (min-width: 1610px)']: {
            width: 1440,
          },

          ['@media (max-width: 1610px)']: {
            width: TAB_FULL_WIDTH,
          },
        },
      },
      {
        type: AddBomPartsToQuoteStep.SELECT_ITEMS_TO_COPY,
        title: selectItemsToCopyTitle,
        content: (
          <SelectItemsToCopy
            ref={itemsToCopyFormRef}
            addToQuoteType={addToQuoteType}
            itemsToCopyProps={itemsToCopyProps}
            entityType={entityType}
          />
        ),
        nextButton: {
          text:
            QuoteType.VENDOR === addToQuoteType
              ? t('buttonLabelAddToVendorQuote', { entityType })
              : t('buttonLabelAddToCustomerQuote', { entityType }),
          options: { onClick: (handleOnClose?: () => void) => onSubmitAddToQuote(handleOnClose) },
        },
        previousButton: {
          options: { onClick: () => setSelectedQuote(null) },
        },
        sx: { width: '400px' },
      },
    ],
    [
      t,
      addToQuoteType,
      entityType,
      selectedContact,
      selectExistingQuoteStep,
      selectedQuote?.id,
      selectItemsToCopyTitle,
      itemsToCopyProps,
      onSubmitAddToQuote,
      setSelectedQuote,
    ]
  );

  return (
    <StepsProvider steps={steps} onClose={handleOnClose}>
      <AddBomPartsToQuoteStepsWithinProvider
        isOpen={isOpen}
        addToQuoteType={addToQuoteType}
        selectedItems={selectedItems}
        entityType={entityType}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />
    </StepsProvider>
  );
};

const AddBomPartsToQuoteStepsWithinProvider = <TFormData,>({
  isOpen,
  addToQuoteType,
  selectedItems,
  entityType = QuoteEntityType.QUOTE,
  isAlternateVersionEnabled = false,
}: Partial<IProps<TFormData>> & { isAlternateVersionEnabled: boolean }) => {
  const { t } = useTranslation();

  const { showWarningNotification } = useNotificationContext();
  const { activeStep } = useStepsContext();

  useEffect(() => {
    if (!isOpen || !isAlternateVersionEnabled) {
      return;
    }

    const abandonedPartsSelected = selectedItems.filter(
      (item) => item.externalPartStatus === ExternalPartStatusType.ABANDONED
    );

    if (!abandonedPartsSelected || abandonedPartsSelected.length === 0) {
      return;
    }

    showWarningNotification(
      {
        message: t('messageCopyPartsWithStatusAbandoned', {
          linesWithStatusAbandoned: abandonedPartsSelected.map((item) => item.line).join(', '),
        }),
        withoutCloseButton: true,
      },
      NotificationPosition.DRAWER
    );
  }, [selectedItems, showWarningNotification, t, activeStep, isAlternateVersionEnabled, isOpen]);

  return (
    <Steps
      title={t('titleAddToQuote', {
        quoteType: upperFirst(addToQuoteType),
        entityType,
      })}
      isOpen={isOpen}
      color={Color.NEUTRAL}
      size={Size.MD}
      fullWidth={false}
      drawerSlotAndSlotProps={{ slotProps: { content: { sx: { width: 'auto' } } } }}
    />
  );
};
